import { api } from "../../api/api";
import { SttLanguage } from "../carScript";
import { MicCapture, PCM_SAMPLE_RATE } from "./micCapture";

/** One thing the driver said, as each recognition language heard it. */
export interface Utterance {
    transcripts: Partial<Record<SttLanguage, string>>;
    /** When the speech began (the first partial transcript), ms since epoch. */
    startedAt: number;
}

export type SpeechStatus = 'off' | 'connecting' | 'listening' | 'error';

export interface SpeechListeners {
    onUtterance: (utterance: Utterance) => void;
    /** What is being heard right now, before the phrase is finished. */
    onPartial: (text: string) => void;
    onStatus: (status: SpeechStatus, detail?: string) => void;
}

/** The microphone turned into utterances; see RealtimeSpeechInput. */
export interface SpeechInput {
    /** Asks for the microphone. Call from a click handler. */
    start(listeners: SpeechListeners): Promise<void>;
    /** The recognition languages to listen in; streams are opened and closed to match. */
    setLanguages(languages: SttLanguage[]): void;
    stop(): void;
}

const REALTIME_URL = 'wss://api.elevenlabs.io/v1/speech-to-text/realtime';
/** Silence that ends a phrase. Shorter cuts slow speakers off; longer delays every answer. */
const VAD_SILENCE_SECONDS = 0.8;
/** How long to wait for the other languages' version of a phrase one stream has finished. */
const MERGE_WINDOW_MS = 700;
const RECONNECT_DELAYS_MS = [500, 1000, 2000, 5000, 10000];

function toBase64(pcm: ArrayBuffer): string {
    const bytes = new Uint8Array(pcm);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 0x8000)
        binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
    return btoa(binary);
}

interface ServerMessage {
    message_type: string;
    text?: string;
    error?: string;
    message?: string;
}

/**
 * One realtime recognition WebSocket in one language. Every connection needs a
 * fresh single-use token from our server; a dropped connection reconnects.
 */
class SttStream {
    private socket: WebSocket | undefined;
    private closed = false;
    private failures = 0;
    private phraseStartedAt: number | undefined;

    constructor(
        readonly language: SttLanguage,
        private readonly onCommitted: (language: SttLanguage, text: string, startedAt: number) => void,
        private readonly onPartial: (text: string) => void,
        private readonly onState: () => void,
    ) {
        void this.connect();
    }

    get open(): boolean {
        return this.socket?.readyState === WebSocket.OPEN;
    }

    send(chunk: string): void {
        if (!this.open)
            return;
        this.socket!.send(JSON.stringify({
            message_type: 'input_audio_chunk',
            audio_base_64: chunk,
            commit: false,
            sample_rate: PCM_SAMPLE_RATE,
        }));
    }

    close(): void {
        this.closed = true;
        this.socket?.close();
        this.socket = undefined;
    }

    private async connect(): Promise<void> {
        if (this.closed)
            return;
        let token: string;
        try {
            token = (await api.sttToken()).token;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Speech recognition token failed', error);
            this.retry();
            return;
        }
        if (this.closed)
            return;

        const params = new URLSearchParams({
            model_id: 'scribe_v2_realtime',
            token,
            audio_format: `pcm_${PCM_SAMPLE_RATE}`,
            commit_strategy: 'vad',
            vad_silence_threshold_secs: String(VAD_SILENCE_SECONDS),
            language_code: this.language,
        });
        const socket = new WebSocket(`${REALTIME_URL}?${params.toString()}`);
        this.socket = socket;

        socket.onopen = () => {
            this.failures = 0;
            this.onState();
        };
        socket.onmessage = event => this.handle(JSON.parse(event.data as string) as ServerMessage);
        socket.onclose = () => {
            if (this.socket === socket) {
                this.socket = undefined;
                this.onState();
                this.retry();
            }
        };
    }

    private retry(): void {
        if (this.closed)
            return;
        const delay = RECONNECT_DELAYS_MS[Math.min(this.failures, RECONNECT_DELAYS_MS.length - 1)];
        this.failures++;
        this.onState();
        setTimeout(() => void this.connect(), delay);
    }

    private handle(message: ServerMessage): void {
        const text = (message.text ?? '').trim();
        switch (message.message_type) {
            case 'session_started':
                return;
            case 'partial_transcript':
                if (text.length > 0) {
                    if (this.phraseStartedAt === undefined)
                        this.phraseStartedAt = Date.now();
                    this.onPartial(text);
                }
                return;
            case 'committed_transcript': {
                const startedAt = this.phraseStartedAt ?? Date.now();
                this.phraseStartedAt = undefined;
                if (text.length > 0)
                    this.onCommitted(this.language, text, startedAt);
                return;
            }
            case 'committed_transcript_with_timestamps':
                return;
            default:
                // Errors (quota, auth, session time limit…) arrive as messages; the server then closes.
                // eslint-disable-next-line no-console
                console.warn('Speech recognition:', message.message_type, message.error ?? message.message ?? '');
        }
    }
}

/**
 * The microphone, streamed to ElevenLabs' realtime recognizer in every
 * language at once: Russian always (the commands), plus the language the
 * answers are in. A single recognizer can't do both — set to Greek it turns
 * "дальше" into "Δάλις", left to guess it hears a short Greek word as Ukrainian.
 * Each stream finishes a phrase on its own; the versions of one phrase are
 * merged into one utterance.
 */
export class RealtimeSpeechInput implements SpeechInput {
    private readonly mic: MicCapture;
    private readonly streams = new Map<SttLanguage, SttStream>();
    private listeners: SpeechListeners | undefined;
    private pending: Utterance | undefined;
    private pendingTimer: ReturnType<typeof setTimeout> | undefined;

    /** Construct inside a click handler (see MicCapture). */
    constructor(private readonly deviceId?: string) {
        this.mic = new MicCapture(chunk => this.send(chunk));
    }

    async start(listeners: SpeechListeners): Promise<void> {
        this.listeners = listeners;
        listeners.onStatus('connecting');
        await this.mic.open(this.deviceId);
    }

    setLanguages(languages: SttLanguage[]): void {
        const wanted = new Set(languages);
        this.streams.forEach((stream, language) => {
            if (!wanted.has(language)) {
                stream.close();
                this.streams.delete(language);
            }
        });
        wanted.forEach(language => {
            if (!this.streams.has(language)) {
                this.streams.set(language, new SttStream(
                    language,
                    (lang, text, startedAt) => this.committed(lang, text, startedAt),
                    text => this.listeners?.onPartial(text),
                    () => this.reportStatus(),
                ));
            }
        });
        this.reportStatus();
    }

    stop(): void {
        this.streams.forEach(stream => stream.close());
        this.streams.clear();
        this.mic.close();
        if (this.pendingTimer !== undefined)
            clearTimeout(this.pendingTimer);
        this.listeners?.onStatus('off');
    }

    private send(pcm: ArrayBuffer): void {
        if (this.streams.size === 0)
            return;
        const chunk = toBase64(pcm);
        this.streams.forEach(stream => stream.send(chunk));
    }

    private reportStatus(): void {
        if (this.listeners === undefined || this.streams.size === 0)
            return;
        const streams = Array.from(this.streams.values());
        this.listeners.onStatus(streams.every(stream => stream.open) ? 'listening' : 'connecting');
    }

    private committed(language: SttLanguage, text: string, startedAt: number): void {
        if (this.pending === undefined) {
            this.pending = { transcripts: {}, startedAt };
            this.pendingTimer = setTimeout(() => this.flush(), MERGE_WINDOW_MS);
        }
        const pending = this.pending;
        const previous = pending.transcripts[language];
        pending.transcripts[language] = previous !== undefined ? `${previous} ${text}` : text;
        pending.startedAt = Math.min(pending.startedAt, startedAt);

        const heardByAll = Array.from(this.streams.keys()).every(lang => pending.transcripts[lang] !== undefined);
        if (heardByAll)
            this.flush();
    }

    private flush(): void {
        if (this.pendingTimer !== undefined)
            clearTimeout(this.pendingTimer);
        this.pendingTimer = undefined;
        const utterance = this.pending;
        this.pending = undefined;
        if (utterance !== undefined)
            this.listeners?.onUtterance(utterance);
    }
}

declare global {
    interface Window {
        /** Set by the e2e tests: Car mode then listens to window.__memkeepSay instead of the microphone. */
        __memkeepFakeSpeech?: boolean;
        __memkeepSay?: (text: string | Partial<Record<SttLanguage, string>>) => void;
        __memkeepSpeechLanguages?: SttLanguage[];
    }
}

/** Speech typed in by the e2e tests through window.__memkeepSay. */
class FakeSpeechInput implements SpeechInput {
    private listeners: SpeechListeners | undefined;
    private languages: SttLanguage[] = [];

    async start(listeners: SpeechListeners): Promise<void> {
        this.listeners = listeners;
        window.__memkeepSay = said => {
            const transcripts: Partial<Record<SttLanguage, string>> = {};
            for (const language of this.languages)
                transcripts[language] = typeof said === 'string' ? said : said[language];
            this.listeners?.onUtterance({ transcripts, startedAt: Date.now() });
        };
        listeners.onStatus('listening');
    }

    setLanguages(languages: SttLanguage[]): void {
        this.languages = languages;
        window.__memkeepSpeechLanguages = languages;
    }

    stop(): void {
        this.listeners?.onStatus('off');
        delete window.__memkeepSay;
    }
}

export function createSpeechInput(deviceId?: string): SpeechInput {
    return window.__memkeepFakeSpeech === true ? new FakeSpeechInput() : new RealtimeSpeechInput(deviceId);
}
